import React, { useMemo, useRef } from "react";
import TableContainer from "components/common/TableContainer";
import { Link } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import {
  CodeCell,
  IndexColumn,
  SourceColumn,
  resolveMappedSourceCell,
} from "components/common/columns";
import { DateTime } from "components/common/Date";
import { formatCurrency } from "utils/currency";
import { Settlement } from "features/ClientAdvanceSettlements/types/client-advance-settlement.types";
import { getClientAdvanceSettlements } from "features/ClientAdvanceSettlements/services/ClientAdvanceSettlementService";
import { ClientAdvanceSettlementSourceTypeLabel } from "features/ClientAdvanceSettlements/constants/client-advance-settlement.constants";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";

export const ClientAdvanceSettlementTable = () => {
  const { can } = useAccess();
  const canReadAdvance = can(Permissions.CLIENT_ADVANCE.READ);
  const tableRef = useRef<() => void>(null);

  const columns: ColumnDef<Settlement>[] = useMemo(
    () => [
      IndexColumn<Settlement>(),
      {
        header: "Code",
        cell: ({ row }) => <CodeCell code={row.original.code} />,
        accessorKey: "code",
        enableColumnFilter: false,
      },
      {
        header: "Client Advance",
        accessorKey: "clientAdvance.code",
        enableColumnFilter: false,
        cell: ({ row }) =>
          canReadAdvance ? (
            <Link to={`/finance/client-advances/${row.original.clientAdvanceId}`}>
              {row.original.clientAdvance?.code ?? `#${row.original.clientAdvanceId}`}
            </Link>
          ) : (
            row.original.clientAdvance?.code ?? `#${row.original.clientAdvanceId}`
          ),
      },
      SourceColumn<Settlement>({
        resolve: (row) =>
          resolveMappedSourceCell(row.sourceType, row.sourceId, {
            typeLabelMap: ClientAdvanceSettlementSourceTypeLabel,
            source: row.source,
          }),
      }),
      {
        header: "Amount",
        accessorKey: "amount",
        enableColumnFilter: false,
        cell: ({ row }) => formatCurrency(row.original.amount),
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) => <DateTime date={row.original.created_at} />,
      },
    ],
    [canReadAdvance],
  );

  return (
    <React.Fragment>
      <TableContainer
        columns={columns}
        fetchApi={getClientAdvanceSettlements}
        pageSize={10}
        globalSearch={true}
        refreshRef={tableRef}
        divClass="table-responsive table-card mb-3"
        tableClass="align-middle table-nowrap mb-0"
        theadClass="table-light text-muted"
      />
    </React.Fragment>
  );
};
